import { DiscordSDK } from '@discord/embedded-app-sdk';

export interface Identity {
  name: string;
  room: string;
}

/** Discord launches the Activity iframe with ?frame_id=…&instance_id=… */
export const inDiscord = (): boolean => new URLSearchParams(location.search).has('frame_id');

/**
 * Inside Discord every request leaves the iframe through Discord's proxy at
 * /.proxy/<path>, which our root URL mapping forwards to the server with the
 * prefix stripped. Outside Discord we talk to the server directly.
 */
export const apiBase = (): string => (inDiscord() ? '/.proxy' : '');

/** OAuth via the Embedded App SDK: authorize → server exchanges the code → authenticate. */
export async function discordIdentity(): Promise<Identity | null> {
  if (!inDiscord()) return null;
  const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID as string | undefined;
  if (!clientId) throw new Error('VITE_DISCORD_CLIENT_ID is not set');

  const sdk = new DiscordSDK(clientId);
  await sdk.ready();

  const { code } = await sdk.commands.authorize({
    client_id: clientId,
    response_type: 'code',
    state: '',
    prompt: 'none',
    scope: ['identify'],
  });

  const res = await fetch(`${apiBase()}/api/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) throw new Error(`Token exchange failed (${res.status})`);
  const { access_token } = (await res.json()) as { access_token: string };

  const auth = await sdk.commands.authenticate({ access_token });
  return {
    name: auth.user.global_name ?? auth.user.username,
    // One voice channel launch = one instance = one match.
    room: sdk.instanceId,
  };
}
